import React, { useMemo, useState } from 'react';
import { useConfig } from '../../context/ConfigContext';
import { useToast } from '../Toast';
import { C, S } from '../../utils/styles';

const US_STATE_OPTIONS = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM',
  'NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA',
  'WV','WI','WY',
];

function summarizeRate(entry) {
  if (!entry) return '—';
  if (entry.brackets) {
    // Compute lo/hi rate across all filings
    const all = Object.values(entry.brackets).flat();
    if (!all.length) return 'Progressive';
    const lo = Math.min(...all.map((b) => b.rate));
    const hi = Math.max(...all.map((b) => b.rate));
    return `${(lo * 100).toFixed(2)}–${(hi * 100).toFixed(2)}%`;
  }
  if (entry.flatRate != null) return `${(entry.flatRate * 100).toFixed(2)}%`;
  return '—';
}

function TypeBadge({ type }) {
  const map = {
    city_resident: { label: 'City (resident)', bg: C.accentBg, color: C.accent },
    county_resident: { label: 'County (resident)', bg: C.purpleLight, color: C.purple },
    occupational: { label: 'Occupational', bg: C.orangeLight, color: C.orange },
    baseline: { label: 'Baseline', bg: C.bg, color: C.textMid },
  };
  const v = map[type] || { label: type || '—', bg: C.bg, color: C.textMid };
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: v.bg, color: v.color }}>{v.label}</span>
  );
}

export default function LocalIncomeTaxEditor() {
  const { config, updateConfig } = useConfig();
  const showToast = useToast();
  const map = config?.localIncomeTaxes || {};

  const [draft, setDraft] = useState(null); // { keyName, entry, isNew }
  const rows = useMemo(() => Object.entries(map).sort(([a], [b]) => a.localeCompare(b)), [map]);

  const persist = (next) => updateConfig({ localIncomeTaxes: next });

  const remove = async (key) => {
    const next = { ...map };
    delete next[key];
    await persist(next);
    showToast(`${key} removed`, 'success');
  };

  const startNew = () => {
    setDraft({
      keyName: '',
      isNew: true,
      entry: {
        type: 'city_resident',
        conformsToFederal: true,
        flatRate: 0.01,
        nonResidentRate: null,
        state: '',
        effectiveDate: '',
        notes: '',
      },
    });
  };

  const startEdit = (key, entry) => {
    setDraft({ keyName: key, isNew: false, entry: JSON.parse(JSON.stringify(entry)) });
  };

  const saveDraft = async () => {
    if (!draft) return;
    const key = draft.keyName.trim();
    if (!key) { showToast('Jurisdiction name is required', 'error'); return; }
    if (!draft.entry.state) { showToast('State is required', 'error'); return; }
    if (draft.entry.brackets == null && (draft.entry.flatRate == null || isNaN(parseFloat(draft.entry.flatRate)))) {
      showToast('Either flatRate or brackets must be set', 'error');
      return;
    }
    const cleaned = {
      type: draft.entry.type || 'city_resident',
      conformsToFederal: !!draft.entry.conformsToFederal,
      state: (draft.entry.state || '').toUpperCase(),
      effectiveDate: draft.entry.effectiveDate || null,
      notes: draft.entry.notes || undefined,
    };
    if (draft.entry.brackets) cleaned.brackets = draft.entry.brackets;
    if (draft.entry.flatRate != null && !isNaN(parseFloat(draft.entry.flatRate))) cleaned.flatRate = parseFloat(draft.entry.flatRate);
    if (draft.entry.nonResidentRate != null && !isNaN(parseFloat(draft.entry.nonResidentRate))) cleaned.nonResidentRate = parseFloat(draft.entry.nonResidentRate);

    const next = { ...map, [key]: cleaned };
    await persist(next);
    setDraft(null);
    showToast(`${key} saved`, 'success');
  };

  return (
    <div data-testid="local-income-tax-editor" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={S.heading}>Local Income Tax</div>
            <p style={{ color: C.textMid, margin: 0, fontSize: 14, maxWidth: 720 }}>
              City-, county-, or occupational-tax jurisdictions that apply on top of state. Most local taxes <strong>conform to federal pre-tax treatment</strong>, meaning Section 125 deductions reduce local taxable income too. NYC alone adds another ~3.08–3.88% of pre-tax savings on top of NY state.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              data-testid="local-tax-sync-btn"
              disabled
              title="Sync via PayrollTaxAPI will be wired in a follow-up phase"
              style={{ ...S.buttonSecondary, opacity: 0.5, cursor: 'not-allowed' }}
            >
              ↻ Sync from PayrollTaxAPI (pending)
            </button>
            <button data-testid="local-tax-add-btn" type="button" onClick={startNew} style={S.buttonPrimary}>+ Add Jurisdiction</button>
          </div>
        </div>
      </div>

      <div style={S.card}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
            <thead>
              <tr>
                <th style={S.th}>Jurisdiction</th>
                <th style={S.th}>Type</th>
                <th style={S.th}>Rate</th>
                <th style={S.th}>State</th>
                <th style={S.th}>Conforms?</th>
                <th style={S.th}>Effective</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td style={{ ...S.td, color: C.textLight, fontStyle: 'italic' }} colSpan={7}>No local jurisdictions configured.</td></tr>
              )}
              {rows.map(([key, entry]) => (
                <tr key={key}>
                  <td style={{ ...S.td, fontWeight: 600 }}>{key}</td>
                  <td style={S.td}><TypeBadge type={entry.type} /></td>
                  <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace" }}>{summarizeRate(entry)}</td>
                  <td style={S.td}>{entry.state || '—'}</td>
                  <td style={S.td}>{entry.conformsToFederal ? '✓' : '—'}</td>
                  <td style={S.td}>{entry.effectiveDate || '—'}</td>
                  <td style={{ ...S.td, textAlign: 'right' }}>
                    <button data-testid={`local-tax-edit-${key}`} type="button" onClick={() => startEdit(key, entry)} style={{ ...S.buttonSecondary, padding: '6px 12px', fontSize: 12 }}>Edit</button>
                    <button data-testid={`local-tax-remove-${key}`} type="button" onClick={() => remove(key)} style={{ ...S.buttonDanger, padding: '6px 12px', fontSize: 12, marginLeft: 6 }}>Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {draft && (
        <div data-testid="local-tax-drawer" style={{ ...S.card, borderColor: C.accent, borderWidth: 2 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={S.heading}>{draft.isNew ? 'New jurisdiction' : `Edit — ${draft.keyName}`}</div>
            <div>
              <button data-testid="local-tax-cancel" type="button" onClick={() => setDraft(null)} style={{ ...S.buttonSecondary, padding: '6px 14px' }}>Cancel</button>
              <button data-testid="local-tax-save" type="button" onClick={saveDraft} style={{ ...S.buttonPrimary, padding: '6px 14px', marginLeft: 8 }}>Save</button>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={S.label}>Jurisdiction key (City/County, ST)</label>
              <input
                data-testid="local-tax-key"
                disabled={!draft.isNew}
                value={draft.keyName}
                onChange={(e) => setDraft({ ...draft, keyName: e.target.value })}
                placeholder="New York City, NY"
                style={S.input}
              />
            </div>
            <div>
              <label style={S.label}>Type</label>
              <select
                data-testid="local-tax-type"
                value={draft.entry.type || 'city_resident'}
                onChange={(e) => setDraft({ ...draft, entry: { ...draft.entry, type: e.target.value } })}
                style={S.select}
              >
                <option value="city_resident">City (resident)</option>
                <option value="county_resident">County (resident)</option>
                <option value="occupational">Occupational</option>
                <option value="baseline">Baseline</option>
              </select>
            </div>
            <div>
              <label style={S.label}>State</label>
              <select
                data-testid="local-tax-state"
                value={draft.entry.state || ''}
                onChange={(e) => setDraft({ ...draft, entry: { ...draft.entry, state: e.target.value } })}
                style={S.select}
              >
                <option value="">—</option>
                {US_STATE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label style={S.label}>Conforms to federal?</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 8, fontSize: 14, color: C.text }}>
                <input
                  data-testid="local-tax-conforms"
                  type="checkbox"
                  checked={!!draft.entry.conformsToFederal}
                  onChange={(e) => setDraft({ ...draft, entry: { ...draft.entry, conformsToFederal: e.target.checked } })}
                />
                Section 125 reduces local taxable income
              </label>
            </div>
            <div>
              <label style={S.label}>Flat Rate (decimal, e.g. 0.0375 = 3.75%)</label>
              <input
                data-testid="local-tax-flat-rate"
                type="number" step="0.0001"
                value={draft.entry.flatRate ?? ''}
                onChange={(e) => setDraft({ ...draft, entry: { ...draft.entry, flatRate: e.target.value === '' ? null : parseFloat(e.target.value) } })}
                style={S.input}
              />
              {draft.entry.brackets && (
                <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>This jurisdiction uses progressive brackets — flat rate is ignored.</div>
              )}
            </div>
            <div>
              <label style={S.label}>Non-Resident Rate (optional)</label>
              <input
                data-testid="local-tax-nonresident-rate"
                type="number" step="0.0001"
                value={draft.entry.nonResidentRate ?? ''}
                onChange={(e) => setDraft({ ...draft, entry: { ...draft.entry, nonResidentRate: e.target.value === '' ? null : parseFloat(e.target.value) } })}
                style={S.input}
              />
            </div>
            <div>
              <label style={S.label}>Effective Date</label>
              <input
                data-testid="local-tax-effective"
                type="date"
                value={draft.entry.effectiveDate ? draft.entry.effectiveDate.slice(0, 10) : ''}
                onChange={(e) => setDraft({ ...draft, entry: { ...draft.entry, effectiveDate: e.target.value } })}
                style={S.input}
              />
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={S.label}>Notes</label>
              <input
                data-testid="local-tax-notes"
                value={draft.entry.notes || ''}
                onChange={(e) => setDraft({ ...draft, entry: { ...draft.entry, notes: e.target.value } })}
                placeholder="Optional clarification — e.g. 'Yonkers approximation: 1.61% flat'"
                style={S.input}
              />
            </div>
          </div>
          {draft.entry.brackets && (
            <div style={{ marginTop: 16, padding: 12, background: C.accentBg, borderRadius: 8, border: `1px solid ${C.accentLight}` }}>
              <div style={{ ...S.heading, color: C.accent, marginBottom: 8 }}>Progressive brackets</div>
              <div style={{ fontSize: 12, color: C.textMid }}>
                Progressive brackets (e.g. NYC) are seeded from the codebase and not editable in the UI yet. Edit the JSON in <code>jurisdictionsSeed.js</code> for now. Flat-rate edits above will be saved alongside the existing bracket schedule.
              </div>
            </div>
          )}
        </div>
      )}

      <div style={{ ...S.card, background: C.accentBg, borderColor: C.accentLight }}>
        <div style={{ ...S.heading, color: C.accent }}>How it's applied</div>
        <p style={{ margin: 0, color: C.textMid, fontSize: 13, lineHeight: 1.6 }}>
          For each employee, the engine resolves their jurisdiction (explicit city &gt; ZIP lookup &gt; employer HQ). If the jurisdiction has a configured local tax and the entry conforms to federal, the engine reduces local taxable income by the annual Section 125 premium and the additional savings flow into the audit trail and the workforce-level summary.
        </p>
      </div>
    </div>
  );
}
